// src/mockData/customerActivity.ts

import { PROMISES } from './promises';
import { DISPUTES } from './disputes';

/**
 * Mock data for customer activity timeline and requires attention items
 * Links to existing customer IDs from customers.ts (ACC-001 through ACC-030)
 */

function toAccountId(customerId: string): string {
  if (customerId.startsWith('ACC-')) return customerId;
  return `ACC-${customerId.padStart(3, '0')}`;
}

// Collection Case type for "Requires Attention"
export interface CollectionCase {
  id: string;
  customerId: string;
  caseNumber: string;
  nextAction: string;
  dueDate: string;
  status: 'active' | 'pending' | 'escalated';
  priority: 'high' | 'medium' | 'low';
}

// Activity type for "Recent Activity"
export interface CustomerActivity {
  id: string;
  customerId: string;
  type: 'email' | 'phone' | 'payment' | 'system' | 'note' | 'promise' | 'dispute' | 'case';
  date: string;
  actor: string;
  title: string;
  description: string;
  metadata?: {
    amount?: number;
    invoices?: string[];
    status?: string;
  };
}

// Mock collection cases data - distributed across customers
export const COLLECTION_CASES: CollectionCase[] = [
  // ACC-001 (Acme Corporation)
  {
    id: 'CASE-001',
    customerId: 'ACC-001',
    caseNumber: 'AR-00025',
    nextAction: 'Schedule payment discussion',
    dueDate: '2026-02-02',
    status: 'active',
    priority: 'high',
  },
  // ACC-002 (Global Tech Industries)
  {
    id: 'CASE-002',
    customerId: 'ACC-002',
    caseNumber: 'AR-00031',
    nextAction: 'Follow up on payment plan',
    dueDate: '2026-02-05',
    status: 'active',
    priority: 'high',
  },
  // ACC-003 (Mega Retail Group)
  {
    id: 'CASE-003',
    customerId: 'ACC-003',
    caseNumber: 'AR-00042',
    nextAction: 'Escalate to legal team',
    dueDate: '2026-02-03',
    status: 'escalated',
    priority: 'high',
  },
  // ACC-005 (Sunrise Enterprises)
  {
    id: 'CASE-004',
    customerId: 'ACC-005',
    caseNumber: 'AR-00018',
    nextAction: 'Review payment history',
    dueDate: '2026-02-04',
    status: 'active',
    priority: 'high',
  },
  // ACC-006 (Quantum Solutions)
  {
    id: 'CASE-005',
    customerId: 'ACC-006',
    caseNumber: 'AR-00027',
    nextAction: 'Send final notice',
    dueDate: '2026-02-06',
    status: 'active',
    priority: 'high',
  },
  // ACC-008 (Pacific Imports)
  {
    id: 'CASE-006',
    customerId: 'ACC-008',
    caseNumber: 'AR-00033',
    nextAction: 'Negotiate settlement',
    dueDate: '2026-02-07',
    status: 'escalated',
    priority: 'high',
  },
  // ACC-011 (Coastal Ventures)
  {
    id: 'CASE-007',
    customerId: 'ACC-011',
    caseNumber: 'AR-00045',
    nextAction: 'Schedule call with CFO',
    dueDate: '2026-02-08',
    status: 'active',
    priority: 'high',
  },
  // ACC-012 (Metro Services Group)
  {
    id: 'CASE-008',
    customerId: 'ACC-012',
    caseNumber: 'AR-00051',
    nextAction: 'Review credit terms',
    dueDate: '2026-02-09',
    status: 'active',
    priority: 'high',
  },
  // ACC-013 (Horizon Technologies)
  {
    id: 'CASE-009',
    customerId: 'ACC-013',
    caseNumber: 'AR-00062',
    nextAction: 'Prepare legal documentation',
    dueDate: '2026-02-10',
    status: 'active',
    priority: 'medium',
  },
  // ACC-007 (BuildRight Construction)
  {
    id: 'CASE-010',
    customerId: 'ACC-007',
    caseNumber: 'AR-00036',
    nextAction: 'Review disputed delivery charges',
    dueDate: '2026-02-11',
    status: 'active',
    priority: 'medium',
  },
  // ACC-016 (Nexus Corporation)
  {
    id: 'CASE-011',
    customerId: 'ACC-016',
    caseNumber: 'AR-00071',
    nextAction: 'Send payment restructuring proposal',
    dueDate: '2026-02-12',
    status: 'active',
    priority: 'high',
  },
  // ACC-017 (Precision Manufacturing)
  {
    id: 'CASE-012',
    customerId: 'ACC-017',
    caseNumber: 'AR-00075',
    nextAction: 'Follow up on disputed invoice',
    dueDate: '2026-02-13',
    status: 'active',
    priority: 'medium',
  },
  // ACC-021 (Titan Enterprises)
  {
    id: 'CASE-013',
    customerId: 'ACC-021',
    caseNumber: 'AR-00070',
    nextAction: 'Escalate overdue balance',
    dueDate: '2026-02-06',
    status: 'escalated',
    priority: 'high',
  },
  // ACC-026 (Vanguard Industries)
  {
    id: 'CASE-014',
    customerId: 'ACC-026',
    caseNumber: 'AR-00080',
    nextAction: 'Final demand before legal proceedings',
    dueDate: '2026-02-05',
    status: 'escalated',
    priority: 'high',
  },
];

// Mock customer activities - comprehensive timeline for each customer
export const CUSTOMER_ACTIVITIES: CustomerActivity[] = [
  // ACC-001 (Acme Corporation) - Recent activities
  {
    id: 'ACT-001',
    customerId: 'ACC-001',
    type: 'email',
    date: '2026-01-30',
    actor: 'Sarah Johnson',
    title: 'Payment reminder sent for INV-001, INV-002',
    description: 'Customer acknowledged receipt',
    metadata: {
      invoices: ['INV-001', 'INV-002'],
    },
  },
  {
    id: 'ACT-002',
    customerId: 'ACC-001',
    type: 'phone',
    date: '2026-01-28',
    actor: 'Mike Chen',
    title: 'Phone call with David Chen (Accounts Payable Manager)',
    description: 'Discussed payment plan for overdue invoices\nINV-001, INV-002',
    metadata: {
      invoices: ['INV-001', 'INV-002'],
    },
  },
  {
    id: 'ACT-003',
    customerId: 'ACC-001',
    type: 'promise',
    date: '2026-01-25',
    actor: 'System',
    title: 'Payment promise recorded',
    description: '£214,000.00 promised by 2025-12-28 (Status: Broken)',
    metadata: {
      amount: 214000,
      status: 'broken',
    },
  },
  {
    id: 'ACT-004',
    customerId: 'ACC-001',
    type: 'payment',
    date: '2026-01-23',
    actor: 'System',
    title: 'Payment received and allocated',
    description: '£100,000.00 applied to INV-001',
    metadata: {
      amount: 100000,
      invoices: ['INV-001'],
    },
  },
  
  // ACC-002 (Global Tech Industries) - Recent activities
  {
    id: 'ACT-005',
    customerId: 'ACC-002',
    type: 'dispute',
    date: '2026-01-29',
    actor: 'System',
    title: 'Dispute created',
    description: 'Billing Discrepancy - £198,000.00 (Priority: High)',
    metadata: {
      amount: 198000,
      status: 'requiring-response',
    },
  },
  {
    id: 'ACT-006',
    customerId: 'ACC-002',
    type: 'email',
    date: '2026-01-27',
    actor: 'Sarah Johnson',
    title: 'Follow-up on broken promise',
    description: 'Sent reminder about missed payment commitment',
  },
  {
    id: 'ACT-007',
    customerId: 'ACC-002',
    type: 'phone',
    date: '2026-01-24',
    actor: 'Emily Rodriguez',
    title: 'Phone call with Finance Director',
    description: 'Discussed outstanding balance and payment options',
  },
  {
    id: 'ACT-008',
    customerId: 'ACC-002',
    type: 'note',
    date: '2026-01-22',
    actor: 'Sarah Johnson',
    title: 'Customer requested payment plan',
    description: 'Customer experiencing cash flow issues, requested 3-month payment plan',
  },
  
  // ACC-003 (Mega Retail Group) - Recent activities
  {
    id: 'ACT-009',
    customerId: 'ACC-003',
    type: 'case',
    date: '2026-01-31',
    actor: 'System',
    title: 'Collection case escalated',
    description: 'Case AR-00042 escalated to legal team',
    metadata: {
      status: 'escalated',
    },
  },
  {
    id: 'ACT-010',
    customerId: 'ACC-003',
    type: 'email',
    date: '2026-01-29',
    actor: 'Sarah Johnson',
    title: 'Final notice sent',
    description: 'Sent final payment notice before legal action',
  },
  {
    id: 'ACT-011',
    customerId: 'ACC-003',
    type: 'dispute',
    date: '2026-01-26',
    actor: 'System',
    title: 'Dispute created',
    description: 'Quality Issues - £187,000.00 (Priority: High)',
    metadata: {
      amount: 187000,
      status: 'requiring-response',
    },
  },
  {
    id: 'ACT-012',
    customerId: 'ACC-003',
    type: 'phone',
    date: '2026-01-23',
    actor: 'David Kim',
    title: 'Phone call with CEO',
    description: 'Urgent discussion about overdue account',
  },
  
  // ACC-005 (Sunrise Enterprises) - Recent activities
  {
    id: 'ACT-013',
    customerId: 'ACC-005',
    type: 'payment',
    date: '2026-01-30',
    actor: 'System',
    title: 'Payment received and allocated',
    description: '£80,000.00 applied to INV-005',
    metadata: {
      amount: 80000,
      invoices: ['INV-005'],
    },
  },
  {
    id: 'ACT-014',
    customerId: 'ACC-005',
    type: 'email',
    date: '2026-01-28',
    actor: 'Mike Chen',
    title: 'Payment confirmation received',
    description: 'Customer confirmed payment processing',
  },
  {
    id: 'ACT-015',
    customerId: 'ACC-005',
    type: 'promise',
    date: '2026-01-25',
    actor: 'System',
    title: 'Payment promise recorded',
    description: '£165,000.00 promised by 2026-01-05 (Status: Broken)',
    metadata: {
      amount: 165000,
      status: 'broken',
    },
  },
  
  // ACC-006 (Quantum Solutions) - Recent activities
  {
    id: 'ACT-016',
    customerId: 'ACC-006',
    type: 'case',
    date: '2026-01-31',
    actor: 'System',
    title: 'Collection case created',
    description: 'Case AR-00027 - Send final notice',
  },
  {
    id: 'ACT-017',
    customerId: 'ACC-006',
    type: 'dispute',
    date: '2026-01-29',
    actor: 'System',
    title: 'Dispute created',
    description: 'Service Not Delivered - £154,000.00 (Priority: High)',
    metadata: {
      amount: 154000,
      status: 'requiring-response',
    },
  },
  {
    id: 'ACT-018',
    customerId: 'ACC-006',
    type: 'phone',
    date: '2026-01-27',
    actor: 'Emily Rodriguez',
    title: 'Phone call with Accounts Manager',
    description: 'Discussed dispute resolution process',
  },
  
  // ACC-008 (Pacific Imports) - Recent activities
  {
    id: 'ACT-019',
    customerId: 'ACC-008',
    type: 'email',
    date: '2026-01-30',
    actor: 'David Kim',
    title: 'Settlement negotiation scheduled',
    description: 'Meeting scheduled for 2026-02-07',
  },
  {
    id: 'ACT-020',
    customerId: 'ACC-008',
    type: 'payment',
    date: '2026-01-26',
    actor: 'System',
    title: 'Payment received and allocated',
    description: '£50,000.00 applied to INV-008',
    metadata: {
      amount: 50000,
      invoices: ['INV-008'],
    },
  },
  {
    id: 'ACT-021',
    customerId: 'ACC-008',
    type: 'case',
    date: '2026-01-24',
    actor: 'System',
    title: 'Collection case escalated',
    description: 'Case AR-00033 escalated - Negotiate settlement',
    metadata: {
      status: 'escalated',
    },
  },
  
  // ACC-011 (Coastal Ventures) - Recent activities
  {
    id: 'ACT-022',
    customerId: 'ACC-011',
    type: 'case',
    date: '2026-01-31',
    actor: 'System',
    title: 'Collection case created',
    description: 'Case AR-00045 - Schedule call with CFO',
  },
  {
    id: 'ACT-023',
    customerId: 'ACC-011',
    type: 'email',
    date: '2026-01-28',
    actor: 'Lisa Anderson',
    title: 'Payment plan proposal sent',
    description: 'Sent payment plan options to customer',
  },
  {
    id: 'ACT-024',
    customerId: 'ACC-011',
    type: 'dispute',
    date: '2026-01-25',
    actor: 'System',
    title: 'Dispute created',
    description: 'Pricing Dispute - £99,000.00 (Priority: High)',
    metadata: {
      amount: 99000,
      status: 'requiring-response',
    },
  },
  
  // ACC-012 (Metro Services Group) - Recent activities
  {
    id: 'ACT-025',
    customerId: 'ACC-012',
    type: 'phone',
    date: '2026-01-30',
    actor: 'James Wilson',
    title: 'Phone call with CFO',
    description: 'Discussed credit terms review',
  },
  {
    id: 'ACT-026',
    customerId: 'ACC-012',
    type: 'case',
    date: '2026-01-28',
    actor: 'System',
    title: 'Collection case created',
    description: 'Case AR-00051 - Review credit terms',
  },
  {
    id: 'ACT-027',
    customerId: 'ACC-012',
    type: 'promise',
    date: '2026-01-25',
    actor: 'System',
    title: 'Payment promise recorded',
    description: '£88,000.00 promised by 2026-01-05 (Status: Broken)',
    metadata: {
      amount: 88000,
      status: 'broken',
    },
  },
  
  // ACC-013 (Horizon Technologies) - Recent activities
  {
    id: 'ACT-028',
    customerId: 'ACC-013',
    type: 'email',
    date: '2026-01-30',
    actor: 'Sarah Johnson',
    title: 'Legal documentation prepared',
    description: 'Legal team prepared documentation for case AR-00062',
  },
  {
    id: 'ACT-029',
    customerId: 'ACC-013',
    type: 'case',
    date: '2026-01-27',
    actor: 'System',
    title: 'Collection case created',
    description: 'Case AR-00062 - Prepare legal documentation',
  },
  {
    id: 'ACT-030',
    customerId: 'ACC-013',
    type: 'dispute',
    date: '2026-01-24',
    actor: 'System',
    title: 'Dispute created',
    description: 'Contract Dispute - £145,000.00 (Priority: Medium)',
    metadata: {
      amount: 145000,
      status: 'requiring-response',
    },
  },

  // ACC-004 (Retail Partners LLC) - Recent activities
  {
    id: 'ACT-031',
    customerId: 'ACC-004',
    type: 'email',
    date: '2026-01-29',
    actor: 'Sarah Johnson',
    title: 'Payment reminder sent',
    description: 'Reminder sent for outstanding balance',
  },
  {
    id: 'ACT-032',
    customerId: 'ACC-004',
    type: 'phone',
    date: '2026-01-26',
    actor: 'Sarah Johnson',
    title: 'Phone call with Accounts team',
    description: 'Confirmed payment will be processed this week',
  },
  {
    id: 'ACT-033',
    customerId: 'ACC-004',
    type: 'payment',
    date: '2026-01-20',
    actor: 'System',
    title: 'Payment received',
    description: '£25,000.00 applied to INV-004',
    metadata: {
      amount: 25000,
      invoices: ['INV-004'],
    },
  },

  // ACC-007 (BuildRight Construction) - Recent activities
  {
    id: 'ACT-034',
    customerId: 'ACC-007',
    type: 'dispute',
    date: '2026-01-30',
    actor: 'System',
    title: 'Dispute created',
    description: 'Quality Issues - £38,500.00 (Priority: High)',
    metadata: {
      amount: 38500,
      status: 'requiring-response',
    },
  },
  {
    id: 'ACT-035',
    customerId: 'ACC-007',
    type: 'email',
    date: '2026-01-27',
    actor: 'Mike Chen',
    title: 'Invoice copy sent',
    description: 'Customer requested copy of INV-007',
  },
  {
    id: 'ACT-036',
    customerId: 'ACC-007',
    type: 'phone',
    date: '2026-01-23',
    actor: 'Mike Chen',
    title: 'Phone call with Project Manager',
    description: 'Discussed disputed delivery charges',
  },

  // ACC-009 (TechFlow Systems) - Recent activities
  {
    id: 'ACT-037',
    customerId: 'ACC-009',
    type: 'email',
    date: '2026-01-31',
    actor: 'Mike Chen',
    title: 'Statement of account sent',
    description: 'Monthly statement for January 2026 delivered',
  },
  {
    id: 'ACT-038',
    customerId: 'ACC-009',
    type: 'payment',
    date: '2026-01-28',
    actor: 'System',
    title: 'Payment received and allocated',
    description: '£45,000.00 applied to INV-009',
    metadata: {
      amount: 45000,
      invoices: ['INV-009'],
    },
  },
  {
    id: 'ACT-039',
    customerId: 'ACC-009',
    type: 'note',
    date: '2026-01-24',
    actor: 'Mike Chen',
    title: 'Customer credit review completed',
    description: 'Annual credit review - risk level unchanged',
  },

  // ACC-010 (Premier Supplies) - Recent activities
  {
    id: 'ACT-040',
    customerId: 'ACC-010',
    type: 'phone',
    date: '2026-01-30',
    actor: 'Mike Chen',
    title: 'Phone call with Finance Manager',
    description: 'Confirmed all invoices are on track for payment',
  },
  {
    id: 'ACT-041',
    customerId: 'ACC-010',
    type: 'email',
    date: '2026-01-25',
    actor: 'Mike Chen',
    title: 'Payment confirmation sent',
    description: 'Confirmed receipt of £35,000.00 payment',
  },

  // ACC-014 (Horizon Trading) - Recent activities
  {
    id: 'ACT-042',
    customerId: 'ACC-014',
    type: 'dispute',
    date: '2026-01-29',
    actor: 'System',
    title: 'Dispute created',
    description: 'Pricing Dispute - £35,000.00 (Priority: High)',
    metadata: {
      amount: 35000,
      status: 'requiring-response',
    },
  },
  {
    id: 'ACT-043',
    customerId: 'ACC-014',
    type: 'email',
    date: '2026-01-26',
    actor: 'Emily Rodriguez',
    title: 'Follow-up on overdue invoices',
    description: 'Sent second reminder for overdue balance',
  },
  {
    id: 'ACT-044',
    customerId: 'ACC-014',
    type: 'phone',
    date: '2026-01-22',
    actor: 'Emily Rodriguez',
    title: 'Phone call with Accounts Payable',
    description: 'Customer cited internal delays for late payment',
  },

  // ACC-015 (Velocity Systems) - Recent activities
  {
    id: 'ACT-045',
    customerId: 'ACC-015',
    type: 'payment',
    date: '2026-01-30',
    actor: 'System',
    title: 'Payment received',
    description: '£60,000.00 applied to INV-015',
    metadata: {
      amount: 60000,
      invoices: ['INV-015'],
    },
  },
  {
    id: 'ACT-046',
    customerId: 'ACC-015',
    type: 'email',
    date: '2026-01-27',
    actor: 'Emily Rodriguez',
    title: 'Payment reminder sent',
    description: 'Standard 30-day reminder for outstanding invoices',
  },

  // ACC-016 (Nexus Corporation) - Recent activities
  {
    id: 'ACT-047',
    customerId: 'ACC-016',
    type: 'case',
    date: '2026-01-31',
    actor: 'System',
    title: 'Collection case review scheduled',
    description: 'Quarterly review of outstanding balance',
  },
  {
    id: 'ACT-048',
    customerId: 'ACC-016',
    type: 'email',
    date: '2026-01-28',
    actor: 'David Kim',
    title: 'Payment plan proposal sent',
    description: 'Proposed 6-month payment plan for outstanding balance',
  },
  {
    id: 'ACT-049',
    customerId: 'ACC-016',
    type: 'phone',
    date: '2026-01-25',
    actor: 'David Kim',
    title: 'Phone call with VP Finance',
    description: 'Discussed restructuring of payment terms',
  },

  // ACC-017 (Precision Manufacturing) - Recent activities
  {
    id: 'ACT-050',
    customerId: 'ACC-017',
    type: 'dispute',
    date: '2026-01-30',
    actor: 'System',
    title: 'Dispute created',
    description: 'Billing Discrepancy - £54,000.00 (Priority: High)',
    metadata: {
      amount: 54000,
      status: 'requiring-response',
    },
  },
  {
    id: 'ACT-051',
    customerId: 'ACC-017',
    type: 'phone',
    date: '2026-01-27',
    actor: 'David Kim',
    title: 'Phone call with Accounts team',
    description: 'Customer disputing charges on recent invoice',
  },

  // ACC-018 (Summit Distributors) - Recent activities
  {
    id: 'ACT-052',
    customerId: 'ACC-018',
    type: 'email',
    date: '2026-01-29',
    actor: 'David Kim',
    title: 'Final payment notice sent',
    description: 'Third reminder for overdue invoices',
  },
  {
    id: 'ACT-053',
    customerId: 'ACC-018',
    type: 'payment',
    date: '2026-01-24',
    actor: 'System',
    title: 'Partial payment received',
    description: '£75,000.00 applied to INV-018',
    metadata: {
      amount: 75000,
      invoices: ['INV-018'],
    },
  },

  // ACC-019 (Apex Solutions) - Recent activities
  {
    id: 'ACT-054',
    customerId: 'ACC-019',
    type: 'note',
    date: '2026-01-30',
    actor: 'David Kim',
    title: 'Customer credit limit review',
    description: 'Recommending credit limit reduction based on payment history',
  },
  {
    id: 'ACT-055',
    customerId: 'ACC-019',
    type: 'phone',
    date: '2026-01-26',
    actor: 'David Kim',
    title: 'Phone call with Finance Director',
    description: 'Discussed upcoming payment schedule',
  },

  // ACC-020 (CoreTech Industries) - Recent activities
  {
    id: 'ACT-056',
    customerId: 'ACC-020',
    type: 'email',
    date: '2026-01-31',
    actor: 'David Kim',
    title: 'Statement of account sent',
    description: 'Monthly statement for January 2026',
  },
  {
    id: 'ACT-057',
    customerId: 'ACC-020',
    type: 'payment',
    date: '2026-01-27',
    actor: 'System',
    title: 'Payment received and allocated',
    description: '£55,000.00 applied to INV-020',
    metadata: {
      amount: 55000,
      invoices: ['INV-020'],
    },
  },

  // ACC-021 (Titan Enterprises) - Recent activities
  {
    id: 'ACT-058',
    customerId: 'ACC-021',
    type: 'case',
    date: '2026-01-31',
    actor: 'System',
    title: 'Collection case created',
    description: 'Case AR-00070 - Escalate overdue balance',
  },
  {
    id: 'ACT-059',
    customerId: 'ACC-021',
    type: 'email',
    date: '2026-01-28',
    actor: 'Lisa Anderson',
    title: 'Final notice sent before legal action',
    description: 'Notified customer of potential legal proceedings',
  },
  {
    id: 'ACT-060',
    customerId: 'ACC-021',
    type: 'phone',
    date: '2026-01-24',
    actor: 'Lisa Anderson',
    title: 'Phone call with CEO',
    description: 'Urgent discussion about overdue account totalling £720,000',
  },

  // ACC-022 (Omega Systems) - Recent activities
  {
    id: 'ACT-061',
    customerId: 'ACC-022',
    type: 'dispute',
    date: '2026-01-29',
    actor: 'System',
    title: 'Dispute created',
    description: 'Service Not Delivered - £64,000.00 (Priority: Critical)',
    metadata: {
      amount: 64000,
      status: 'requiring-response',
    },
  },
  {
    id: 'ACT-062',
    customerId: 'ACC-022',
    type: 'email',
    date: '2026-01-26',
    actor: 'Lisa Anderson',
    title: 'Follow-up on broken promise',
    description: 'Customer missed promised payment date',
  },

  // ACC-023 (Fusion Technologies) - Recent activities
  {
    id: 'ACT-063',
    customerId: 'ACC-023',
    type: 'payment',
    date: '2026-01-30',
    actor: 'System',
    title: 'Payment received',
    description: '£90,000.00 applied to INV-023',
    metadata: {
      amount: 90000,
      invoices: ['INV-023'],
    },
  },
  {
    id: 'ACT-064',
    customerId: 'ACC-023',
    type: 'phone',
    date: '2026-01-27',
    actor: 'Lisa Anderson',
    title: 'Phone call with Accounts Payable',
    description: 'Confirmed remaining balance will be settled by month end',
  },

  // ACC-024 (Catalyst Group) - Recent activities
  {
    id: 'ACT-065',
    customerId: 'ACC-024',
    type: 'email',
    date: '2026-01-29',
    actor: 'Lisa Anderson',
    title: 'Payment reminder sent',
    description: 'Second reminder for overdue invoices',
  },
  {
    id: 'ACT-066',
    customerId: 'ACC-024',
    type: 'note',
    date: '2026-01-25',
    actor: 'Lisa Anderson',
    title: 'Customer experiencing cash flow issues',
    description: 'Requested 30-day extension on payment terms',
  },

  // ACC-025 (Pinnacle Corp) - Recent activities
  {
    id: 'ACT-067',
    customerId: 'ACC-025',
    type: 'payment',
    date: '2026-01-31',
    actor: 'System',
    title: 'Payment received and allocated',
    description: '£48,000.00 applied to INV-025',
    metadata: {
      amount: 48000,
      invoices: ['INV-025'],
    },
  },
  {
    id: 'ACT-068',
    customerId: 'ACC-025',
    type: 'email',
    date: '2026-01-28',
    actor: 'Lisa Anderson',
    title: 'Payment confirmation sent',
    description: 'Confirmed receipt of latest payment',
  },

  // ACC-026 (Vanguard Industries) - Recent activities
  {
    id: 'ACT-069',
    customerId: 'ACC-026',
    type: 'case',
    date: '2026-01-31',
    actor: 'System',
    title: 'Collection case escalated',
    description: 'Case AR-00080 escalated to senior management',
    metadata: {
      status: 'escalated',
    },
  },
  {
    id: 'ACT-070',
    customerId: 'ACC-026',
    type: 'phone',
    date: '2026-01-28',
    actor: 'James Wilson',
    title: 'Phone call with CFO',
    description: 'Final negotiation on payment restructuring',
  },
  {
    id: 'ACT-071',
    customerId: 'ACC-026',
    type: 'email',
    date: '2026-01-24',
    actor: 'James Wilson',
    title: 'Legal notice prepared',
    description: 'Legal team drafted demand letter for outstanding balance',
  },

  // ACC-027 (Zenith Partners) - Recent activities
  {
    id: 'ACT-072',
    customerId: 'ACC-027',
    type: 'dispute',
    date: '2026-01-30',
    actor: 'System',
    title: 'Dispute created',
    description: 'Billing Discrepancy - £72,000.00 (Priority: Critical)',
    metadata: {
      amount: 72000,
      status: 'requiring-response',
    },
  },
  {
    id: 'ACT-073',
    customerId: 'ACC-027',
    type: 'email',
    date: '2026-01-27',
    actor: 'James Wilson',
    title: 'Statement of account sent',
    description: 'Monthly statement with breakdown of disputed items',
  },

  // ACC-028 (Momentum Solutions) - Recent activities
  {
    id: 'ACT-074',
    customerId: 'ACC-028',
    type: 'phone',
    date: '2026-01-30',
    actor: 'James Wilson',
    title: 'Phone call with Finance Manager',
    description: 'Discussed payment plan for overdue invoices',
  },
  {
    id: 'ACT-075',
    customerId: 'ACC-028',
    type: 'payment',
    date: '2026-01-26',
    actor: 'System',
    title: 'Partial payment received',
    description: '£100,000.00 applied to INV-028',
    metadata: {
      amount: 100000,
      invoices: ['INV-028'],
    },
  },

  // ACC-029 (Synergy Enterprises) - Recent activities
  {
    id: 'ACT-076',
    customerId: 'ACC-029',
    type: 'email',
    date: '2026-01-29',
    actor: 'James Wilson',
    title: 'Payment reminder sent',
    description: 'Reminder for overdue invoices totalling £330,000',
  },
  {
    id: 'ACT-077',
    customerId: 'ACC-029',
    type: 'note',
    date: '2026-01-25',
    actor: 'James Wilson',
    title: 'Internal review note',
    description: 'Customer flagged for potential credit limit reduction',
  },

  // ACC-030 (Vertex Corporation) - Recent activities
  {
    id: 'ACT-078',
    customerId: 'ACC-030',
    type: 'payment',
    date: '2026-01-31',
    actor: 'System',
    title: 'Payment received and allocated',
    description: '£70,000.00 applied to INV-030',
    metadata: {
      amount: 70000,
      invoices: ['INV-030'],
    },
  },
  {
    id: 'ACT-079',
    customerId: 'ACC-030',
    type: 'email',
    date: '2026-01-28',
    actor: 'James Wilson',
    title: 'Statement of account sent',
    description: 'Monthly statement for January 2026',
  },
];

/**
 * Get broken promises for a specific customer
 */
export function getCustomerBrokenPromises(customerId: string) {
  const accountId = toAccountId(customerId);
  return PROMISES.filter(
    (promise) => promise.customerId === accountId && promise.status === 'broken'
  );
}

/**
 * Get active disputes for a specific customer
 */
export function getCustomerActiveDisputes(customerId: string) {
  const accountId = toAccountId(customerId);
  return DISPUTES.filter(
    (dispute) => dispute.customerId === accountId && dispute.status === 'requiring-response'
  );
}

/**
 * Get active collection cases for a specific customer
 */
export function getCustomerCollectionCases(customerId: string) {
  const accountId = toAccountId(customerId);
  return COLLECTION_CASES.filter((case_) => case_.customerId === accountId);
}

/**
 * Get recent activities for a specific customer
 */
export function getCustomerActivities(customerId: string, limit?: number) {
  const accountId = toAccountId(customerId);
  const activities = CUSTOMER_ACTIVITIES.filter((activity) => activity.customerId === accountId)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  return limit ? activities.slice(0, limit) : activities;
}

/**
 * Get all attention items for a customer (broken promises, disputes, cases)
 */
export function getCustomerAttentionItems(customerId: string) {
  return {
    brokenPromises: getCustomerBrokenPromises(customerId),
    activeDisputes: getCustomerActiveDisputes(customerId),
    collectionCases: getCustomerCollectionCases(customerId),
  };
}
